import { applyTax, TaxStrategy } from '@principle-theorem/accounting';
import { AtLeast, Region, uid } from '@principle-theorem/shared';
import { isUndefined } from 'lodash';
import {
  IMohServiceCodeExtendedData,
  isMohServiceCodeExtendedData,
  MohClaimType,
} from '../../../../transaction/moh/moh';
import { ChartableSurface } from '../../../core/chartable-surface';
import {
  IPricingRule,
  IPricingRuleItem,
  PricingRuleType,
} from '../../../fees/pricing-rules/pricing-rule-provider';
import {
  IServiceCode,
  IServiceProvider,
  ItemCodeAliases,
  ServiceCode,
  ServiceCodeType,
} from '../../service-item';
import { IPricedServiceCodeEntry } from '../ada-code';
import { MOH_CAPITATION_BANDS } from './moh-capitation-bands';

const MOH_ANNUAL_CONSULTATION_CODE = 'CON1';

type MohServiceCode = Record<
  string,
  AtLeast<IServiceCode<IMohServiceCodeExtendedData>, 'code' | 'title'>
>;

const COM1_BAND = MOH_CAPITATION_BANDS.COM1;
const COM2_BAND = MOH_CAPITATION_BANDS.COM2;
const COM3_BAND = MOH_CAPITATION_BANDS.COM3;

const MOH_BASE_CODES: MohServiceCode = {
  // Standard Package Codes
  CON1: {
    code: MOH_ANNUAL_CONSULTATION_CODE,
    title: 'Annual consultation',
    pricingRule: buildVariablePricingRule([
      { price: COM1_BAND.fee, label: COM1_BAND.code },
      { price: COM2_BAND.fee, label: COM2_BAND.code },
      { price: COM3_BAND.fee, label: COM3_BAND.code },
    ]),
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA],
    },
  },
  CON2: {
    code: 'CON2',
    title: 'Scheduled consultation (6-monthly)',
    fee: 0,
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA],
    },
  },
  CON3: {
    code: 'CON3',
    title: 'Initial consultation (normal hrs, non-enrolled)',
    fee: 92.88,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  SCL1: {
    code: 'SCL1',
    title: 'Removal of supragingival calculus',
    fee: 0,
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA],
    },
  },
  TOP1: {
    code: 'TOP1',
    title: 'Topical fluoride application',
    fee: 0,
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA],
    },
  },
  OPT1: {
    code: 'OPT1',
    title: 'Other preventive treatment',
    fee: 0,
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA],
    },
  },
  PBW1: {
    code: 'PBW1',
    title: 'Bitewing radiograph',
    pricingRule: buildVariablePricingRule([
      { price: 0, label: 'Standard Package' },
      { price: 12.75, label: 'Additional Treatment' },
    ]),
    extendedData: {
      inStandardPackage: true,
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  CON4: {
    code: 'CON4',
    title: 'Emergency consultation after hours',
    fee: 137.16,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Radiographs - shared between OHSA and SDS
  RAD1: {
    code: 'RAD1',
    title: 'Periapical radiograph',
    pricingRule: buildVariablePricingRule([
      { price: 0, label: 'Standard Package' },
      { price: 12.75, label: 'Additional Treatment' },
    ]),
    extendedData: {
      inStandardPackage: true,
      preApprovalClaimTypes: [MohClaimType.HighCaries],
      claimTypes: [
        MohClaimType.OHSA,
        MohClaimType.SDS,
        MohClaimType.HighCaries,
      ],
    },
  },
  RAD2: {
    code: 'RAD2',
    title: 'Panoramic radiograph',
    fee: 49.59,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  RAD3: {
    code: 'RAD3',
    title: 'Occlusal radiograph',
    fee: 33.38,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Fillings - shared between OHSA and SDS (FIL1 has different fees for different contexts)
  FIL1: {
    code: 'FIL1',
    title: 'One-surface restoration, posterior',
    pricingRule: buildVariablePricingRule([
      { price: 0, label: 'Standard Package' },
      { price: 85.23, label: 'Additional Treatment' },
    ]),
    extendedData: {
      inStandardPackage: true,
      preApprovalClaimTypes: [MohClaimType.HighCaries],
      claimTypes: [
        MohClaimType.OHSA,
        MohClaimType.SDS,
        MohClaimType.HighCaries,
      ],
    },
  },
  FIL2: {
    code: 'FIL2',
    title: 'Two-surface posterior restoration',
    fee: 111.75,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  FIL3: {
    code: 'FIL3',
    title: 'Three-surface posterior restoration',
    fee: 137.54,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  FIL4: {
    code: 'FIL4',
    title: 'Complex coronal reconstruction',
    fee: 154.38,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  FIL5: {
    code: 'FIL5',
    title: 'One-surface anterior restoration',
    fee: 101.3,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  FIL6: {
    code: 'FIL6',
    title: 'Multi-surface anterior restoration',
    fee: 136.21,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Fissure Sealant - free in standard package, has fee in High Caries
  FIS1: {
    code: 'FIS1',
    title: 'Fissure sealant',
    pricingRule: buildVariablePricingRule([
      { price: 0, label: 'Standard Package' },
      { price: 28.23, label: 'Additional Treatment' },
    ]),
    extendedData: {
      inStandardPackage: true,
      preApprovalClaimTypes: [MohClaimType.HighCaries],
      claimTypes: [MohClaimType.OHSA, MohClaimType.HighCaries],
    },
  },

  // Crowns - shared between OHSA and SDS
  CRN1: {
    code: 'CRN1',
    title: 'Pre-formed metal crown',
    fee: 83.75,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Extractions
  EXT1: {
    code: 'EXT1',
    title: 'Extraction with local anaesthetic',
    fee: 165.63,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  EXT3: {
    code: 'EXT3',
    title: 'Extraction with general anaesthetic',
    fee: 113.38,
    extendedData: {
      claimTypes: [MohClaimType.SDS],
    },
  },

  // Root Canal Treatments - shared between OHSA and SDS
  RCT1: {
    code: 'RCT1',
    title: 'Root-canal ant / premolar (per canal)',
    fee: 330.85,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  RCT2: {
    code: 'RCT2',
    title: 'Root-canal deciduous tooth',
    fee: 181.51,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  RCT3: {
    code: 'RCT3',
    title: 'Pulpotomy deciduous',
    fee: 116.41,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  // Retired in the 2025/26 Combined Dental Agreement; kept for historical charting.
  RCT4: {
    code: 'RCT4',
    title: 'Pulpotomy permanent',
    fee: 116.41,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  RCT5: {
    code: 'RCT5',
    title: 'Root-canal molar (per canal)',
    fee: 330.85,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Other treatments
  EMD1: {
    code: 'EMD1',
    title: 'Emergency dressing',
    fee: 34.3,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  RCM1: {
    code: 'RCM1',
    title: 'Re-cement inlay / crown',
    fee: 29.99,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  PUL1: {
    code: 'PUL1',
    title: 'Pulpotomy in permanent tooth (full or partial)',
    fee: 350,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  PC1: {
    code: 'PC1',
    title: 'Pulp cap (direct or indirect)',
    fee: 150,
    extendedData: {
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Prior Approval Required - shared between OHSA and SDS
  MSO1: {
    code: 'MSO1',
    title: 'Minor surgery – first half-hour',
    fee: 102.97,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  MSO2: {
    code: 'MSO2',
    title: 'Minor surgery – each quarter-hour',
    fee: 51.46,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  PDT1: {
    code: 'PDT1',
    title: 'Treatment of periodontal disease',
    fee: 80.89,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  SDF1: {
    code: 'SDF1',
    title: 'Silver diamine fluoride first application to 1 to 3 teeth',
    fee: 99.97,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  SDF2: {
    code: 'SDF2',
    title: 'Silver diamine fluoride second application to 1 to 3 teeth',
    fee: 49.96,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  SDF3: {
    code: 'SDF3',
    title: 'Silver diamine fluoride first application to 4 or more teeth',
    fee: 149.93,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  SDF4: {
    code: 'SDF4',
    title: 'Silver diamine fluoride second application to 4 or more teeth',
    fee: 99.97,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Dentures - shared between OHSA and SDS
  DEN3: {
    code: 'DEN3',
    title: 'Acrylic partial denture',
    fee: 807.82,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  DEN4: {
    code: 'DEN4',
    title: 'Acrylic partial denture – extra tooth',
    fee: 62.64,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  DEN5: {
    code: 'DEN5',
    title: 'Acrylic partial denture – clasp',
    fee: 32.54,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  DEN6: {
    code: 'DEN6',
    title: 'Full denture – upper OR lower',
    fee: 1145.07,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  DEN7: {
    code: 'DEN7',
    title: 'Full dentures – upper AND lower',
    fee: 2531.23,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Crowns requiring prior approval - shared between OHSA and SDS
  CRN4: {
    code: 'CRN4',
    title: 'Gold crown',
    fee: 1024.76,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  CRN5: {
    code: 'CRN5',
    title: 'Composite full-coverage crown',
    fee: 213.3,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Posts and Cores - shared between OHSA and SDS
  PST1: {
    code: 'PST1',
    title: 'Cast post and core',
    fee: 289.08,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },
  PST2: {
    code: 'PST2',
    title: 'Pre-formed post and core',
    fee: 120.02,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Veneers - shared between OHSA and SDS
  VEN2: {
    code: 'VEN2',
    title: 'Labial composite veneer',
    fee: 143.45,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Bite Splints - shared between OHSA and SDS
  SPLT: {
    code: 'SPLT',
    title: 'Bite splint',
    fee: 542.09,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // Apexification - shared between OHSA and SDS
  APX1: {
    code: 'APX1',
    title: 'Apexification / open-apex filling',
    fee: 181.51,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
      claimTypes: [MohClaimType.OHSA, MohClaimType.SDS],
    },
  },

  // OHSA Exclusive Codes (Prior Approval Required)
  DEN1: {
    code: 'DEN1',
    title: 'Precision-cast metal partial denture',
    fee: 1858.29,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },
  DEN2: {
    code: 'DEN2',
    title: 'Precision-cast denture – each extra tooth',
    fee: 62.57,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },
  ABMT: {
    code: 'ABMT',
    title: 'Adhesive bridge (Maryland)',
    fee: 1247.02,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },
  CRN2: {
    code: 'CRN2',
    title: 'Ceramic-to-metal crown',
    fee: 1080.83,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },
  CRN3: {
    code: 'CRN3',
    title: 'All-ceramic crown',
    fee: 1078.05,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },
  VEN1: {
    code: 'VEN1',
    title: 'Porcelain veneer',
    fee: 929.09,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.OHSA],
      claimTypes: [MohClaimType.OHSA],
    },
  },

  // High Caries Exclusive Code
  CON5: {
    code: 'CON5',
    title:
      'Consultation, exam/diagnosis, prophylaxis + any tests & BW radiographs',
    fee: 84.51,
    extendedData: {
      preApprovalClaimTypes: [MohClaimType.HighCaries],
      claimTypes: [MohClaimType.HighCaries],
    },
  },
};

export const MOH_CODE_MAP: ItemCodeAliases = Object.entries(
  MOH_BASE_CODES
).reduce(
  (codes, [key, code]) => ({
    ...codes,
    [key]: {
      ...code,
      scope: [ChartableSurface.Unscoped],
      rules: {},
      deleted: false,
      taxStatus: TaxStrategy.GSTApplicable,
      fee: !isUndefined(code.fee)
        ? applyTax(code.fee, Region.NewZealand)
        : undefined,
      type: ServiceCodeType.MOH,
    },
  }),
  {}
);

export const MOH_CODES: IServiceCode[] = Object.values(MOH_CODE_MAP);

export const MOH_SERVICE: IServiceProvider = {
  label: 'MOH',
  description: 'Ministry of Health (OHSA/SDS)',
  type: ServiceCodeType.MOH,
  items: MOH_CODE_MAP,
  region: Region.NewZealand,
};

type VariablePricingRuleItemData = Required<
  Pick<IPricingRuleItem, 'price' | 'label'>
>;

export function buildVariablePricingRule(
  items: VariablePricingRuleItemData[]
): IPricingRule {
  return {
    type: PricingRuleType.Variable,
    ruleItems: getPricingRuleItems(items),
  };
}

export function getPricingRuleItems(
  items: VariablePricingRuleItemData[]
): IPricingRuleItem[] {
  return items.map((item) => ({
    price: applyTax(item.price, Region.NewZealand),
    label: item.label,
    uid: uid(true),
    selected: false,
  }));
}

export function isMohAnnualConsultation(
  serviceCode: IPricedServiceCodeEntry
): boolean {
  return (
    serviceCode.code === MOH_ANNUAL_CONSULTATION_CODE &&
    serviceCode.pricingRule.type === PricingRuleType.Variable
  );
}

export function getMohCodeExtendedData(
  code?: ServiceCode
): IMohServiceCodeExtendedData | undefined {
  const extendedData = isUndefined(code)
    ? undefined
    : MOH_CODE_MAP[code]?.extendedData;

  return isMohServiceCodeExtendedData(extendedData) ? extendedData : undefined;
}

export function requiresMohPreApproval(
  serviceCode?: AtLeast<IServiceCode, 'code'>,
  claimType?: MohClaimType
): boolean {
  const preApprovalClaimTypes =
    getMohCodeExtendedData(serviceCode?.code)?.preApprovalClaimTypes ?? [];

  return claimType
    ? preApprovalClaimTypes.includes(claimType)
    : preApprovalClaimTypes.length > 0;
}
